import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface HealthCheckResult {
  status: string;
  timestamp: string;
  environment?: Record<string, unknown>;
}

interface TestResult {
  success: boolean;
  message: string;
  error?: string;
  details?: unknown;
}

const FormDebugger = () => {
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [results, setResults] = useState<Record<string, HealthCheckResult | TestResult>>({});

  const testFunction = async (functionName: string, isHealthCheck = true, testData?: any) => {
    setLoading(prev => ({ ...prev, [functionName]: true }));
    
    try {
      if (isHealthCheck) {
        const response = await fetch(`https://plizcqmxjmtzdgmmhtxj.supabase.co/functions/v1/${functionName}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBsaXpjcW14am10emRnbW1odHhqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQyNzg2NDIsImV4cCI6MjA4OTYzODY0Mn0.5yTxW8eQVla2HSlctuuvMaOddnMw5vf5sWraAZx67FE`,
            'Content-Type': 'application/json',
          },
        });
        
        const result: HealthCheckResult = await response.json();
        setResults(prev => ({ ...prev, [functionName]: result }));
        
        if (response.ok) {
          toast.success(`${functionName} health check passed`);
        } else {
          toast.error(`${functionName} health check failed`);
        }
      } else if (testData) {
        const { data, error } = await supabase.functions.invoke(functionName, {
          body: testData
        });
        
        const result: TestResult = { 
          success: !error, 
          message: error ? error.message : 'Test completed successfully',
          error: error?.message,
          details: data
        };
        setResults(prev => ({ ...prev, [`${functionName}_test`]: result }));
        
        if (!error) {
          toast.success(`${functionName} test passed`);
        } else {
          toast.error(`${functionName} test failed: ${error.message}`);
        }
      }
    } catch (error: any) {
      console.error(`Error testing ${functionName}:`, error);
      const errorResult: TestResult = {
        success: false,
        message: 'Test failed',
        error: error.message
      };
      setResults(prev => ({ ...prev, [functionName]: errorResult }));
      toast.error(`${functionName} test failed: ${error.message}`);
    } finally {
      setLoading(prev => ({ ...prev, [functionName]: false }));
    }
  };

  const testEarlyAdopterForm = () => {
    const testData = {
      email: 'test@example.com',
      firstName: 'Test',
      lastName: 'User',
      role: 'family_member',
      primaryInterest: 'Testing the early adopter form',
      urgencyLevel: 'exploring',
      phone: '555-0123',
      message: 'This is a test submission',
      source: 'debug-test',
      pagePath: '/debug'
    };
    
    testFunction('early-adopter-signup', false, testData);
  };

  const testProfessionalForm = () => {
    const testData = {
      profession: 'OT',
      firstName: 'Professional',
      lastName: 'Tester',
      email: 'professional-test@example.com',
      phone: '555-0123',
      licenseNumber: 'OT12345',
      licenseState: 'CA',
      specialties: ['home_mods', 'fall_prevention'],
      yearsInPractice: 5,
      serviceRadius: 25,
      website: 'https://example.com',
      source: 'debug-test'
    };
    
    testFunction('submit-professional-application', false, testData);
  };

  const functions = [
    'early-adopter-signup',
    'submit-professional-application',
    'send-form-notifications'
  ];

  return (
    <div className="p-6 space-y-6">
      <h2 className="text-2xl font-bold">Form Debugging Dashboard</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {functions.map(func => (
          <Card key={func}>
            <CardHeader>
              <CardTitle className="text-lg">{func}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button 
                onClick={() => testFunction(func, true)}
                disabled={loading[func]}
                className="w-full"
              >
                Health Check
              </Button>
              
              {results[func] && (
                <div className="space-y-2">
                  <Badge variant={'error' in results[func] && results[func].error ? "destructive" : "default"}>
                    {'error' in results[func] && results[func].error ? "Error" : "OK"}
                  </Badge>
                  <pre className="text-xs bg-muted p-2 rounded overflow-auto">
                    {JSON.stringify(results[func], null, 2)}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Form Integration Tests</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button 
              onClick={testEarlyAdopterForm}
              disabled={loading['early-adopter-signup']}
            >
              Test Early Adopter Form
            </Button>
            
            <Button 
              onClick={testProfessionalForm}
              disabled={loading['submit-professional-application']}
            >
              Test Professional Form
            </Button>
          </div>

          {Object.entries(results).filter(([key]) => key.includes('_test')).map(([key, result]) => (
            <div key={key} className="space-y-2">
              <h4 className="font-bold">{key}</h4>
              <Badge variant={'success' in result && result.success ? "default" : "destructive"}>
                {'success' in result && result.success ? "Success" : "Failed"}
              </Badge>
              <pre className="text-xs bg-muted p-2 rounded overflow-auto">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          ))}
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Recent Database Data</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm space-y-2">
            <p>Early Adopters (last 7 days): 3</p>
            <p>Professional Applications (last 7 days): Check admin panel</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FormDebugger;